import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
import Users from './components/Users';
import Settings from './components/Settings';
import AdminDashboard from './components/AdminDashboard';
import LoginPage from './components/Login';
import Wallet from './components/WalletUpdate';
import DepositPage from './components/DepositPage';
import WithdrawPage from './components/WithdrawPage';
import House from './components/House'
import History from './components/History';
import OriginalGames from './components/originalgames/OriginalGames';
import Game from './components/Games';
import GamesLog from './components/WinSetting';
import SEO from './components/SeoManager';
import Referal from './components/Refrel';
import Bonus from './components/Bonus';
import NotificationSettings from './components/NotificationSettings';
import WinSettings from './components/WinSetting';
import ClubMemberShip from './components/ClubMembership';
import Banner from './components/Banner';
import BankDetails from './components/BankDetails';
import { Buffer } from 'buffer';
// import SportsBetting from './components/SportsBetting'; // sports hidden everywhere
import RedeemCode from './components/RedeemCode';
import ExchangeRate from './components/ExchangeRate';
import Notification from './components/notification';
import SwapHistory from './components/SwapHistory';
import Reports from './components/Reports';
import KYC from './components/UserKyc';
import TierManagement from './components/tier-branch/TierManagement';
import BranchManagement from './components/tier-branch/BranchManagement';
import BankingPage from './components/banking/BankingPage';
import BranchLogin from './components/branch-portal/BranchLogin';
import BranchLayout from './components/branch-portal/BranchLayout';
import BranchDashboard from './components/branch-portal/BranchDashboard';
import BranchDeposits from './components/branch-portal/BranchDeposits';
import BranchWithdrawals from './components/branch-portal/BranchWithdrawals';
import BranchAutoWithdrawals from './components/branch-portal/BranchAutoWithdrawals';
import BranchStaff from './components/branch-portal/BranchStaff';
import BranchAccounts from './components/branch-portal/BranchAccounts';
import BranchBankAccounts from './components/branch-portal/BranchBankAccounts';
import BranchReports from './components/branch-portal/BranchReports';
import AdminManagementLayout from './components/AdminManagement';
import DashboardTab from './components/admin-management/DashboardTab';

import SettingsTab from './components/admin-management/SettingsTab';
import MyAccountTab from './components/admin-management/MyAccountTab';
import BetListTab from './components/admin-management/BetListTab';
import BetListLiveTab from './components/admin-management/BetListLiveTab';

import CasinoTab from './components/admin-management/CasinoTab';
import RiskManagementTab from './components/admin-management/RiskManagementTab';
import ImportTab from './components/admin-management/ImportTab';
import MessageTab from './components/admin-management/MessageTab';
import GameCenterTab from './components/admin-management/GameCenterTab';
import AgentListingTab from './components/admin-management/AgentListingTab';
import TransferTab from './components/admin-management/TransferTab';
import CasinoSettlementTab from './components/admin-management/CasinoSettlementTab';
// import SportsSettlementTab from './components/admin-management/SportsSettlementTab'; // sports hidden everywhere
import AccessManagementTab from './components/admin-management/AccessManagementTab';
import RequirePermission from './components/RequirePermission';
import GameWinLossToggle from './components/GameWinLossToggle';
import UserConfig from './components/UserConfig';
import SiteConfig from './components/SiteConfig';
import PercentageHierarchyPage from './components/PercentageHierarchyPage';
import MarketHistory from './components/MarketWins';
import FanWins from './components/FanwinsHistory';
import MarketInternalSettle from './components/matchsettlement';
import FanInternalSettle from './components/FanmanualSettle';
import FancyResultNotShowingFromApi from './components/FancyResult';
import GiftCardAdmin from './components/AdminGiftcards';
import TurnoverReport from './components/TurnoverReport';
import VaultPro from './components/vaultpro';
import PeerTrade from './components/PeerTrade';
// import SportsDashboard from './components/sportsbetStats'; // sports hidden everywhere
import FancySettlement from './components/FancySettlement';
// import SportsConfigSection from './components/SportsConfig'; // sports hidden everywhere
import SpinWheel from './components/spinWheel';
import Challenges from './components/Challenges';
import BlogAdmin from './components/Blog';
import Promotion from './components/Promotion';
import AccountStatement from './components/AccountStatement';
// Legacy gis-API gaming components replaced by the unified jsGames InnerHousePriority (per GAMING_ROUTES.md)
// import ProvidersPriority from './components/ProvidersPriority';
// import VendorPriority from './components/VendorPriority';
// import TypePriority from './components/TypePriority';
import InnerHousePriority from './components/InnerHousePriority';
import { usePermissions } from './hooks/usePermissions';

/* Sends "/" to the first page the signed-in user may open. Permissions arrive
   asynchronously, so hold the redirect until they land — navigating early
   would bounce an executive to the fallback before their grants are known. */
const HomeRedirect: React.FC = () => {
  const { landingPath, loading } = usePermissions();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#2B6EF5' }} />
      </Box>
    );
  }
  return <Navigate to={landingPath} replace />;
};

function App() {
  window.Buffer = Buffer;
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const handleStorageChange = () => {
      setToken(localStorage.getItem('token'));
    };

    // Listen for changes in localStorage
    window.addEventListener('storage', handleStorageChange);

    // Initial check in case the value is set before the listener is attached
    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const ProtectedRoutes = () => {
    // Re-check token on each render of ProtectedRoutes
    const currentToken = localStorage.getItem('token');
    return currentToken ? <Outlet /> : <Navigate to="/login" replace />;
  };

  return (
    <BrowserRouter>
      <Routes>
        {/*  ───────── public page ───────── */}
        <Route path="/login" element={<LoginPage />} />

        {/* Branch portal — separate login + layout + token (branchToken) */}
        <Route path="/branch/login" element={<BranchLogin />} />
        <Route path="/branch" element={<BranchLayout />}>
          <Route index element={<Navigate to="/branch/dashboard" replace />} />
          <Route path="dashboard" element={<BranchDashboard />} />
          <Route path="deposits" element={<BranchDeposits />} />
          <Route path="withdrawals" element={<BranchWithdrawals />} />
          <Route path="auto-withdrawals" element={<BranchAutoWithdrawals />} />
          <Route path="staff" element={<BranchStaff />} />
          <Route path="executives" element={<BranchStaff />} />
          <Route path="accounts" element={<BranchAccounts />} />
          <Route path="bank-pool" element={<BranchBankAccounts />} />
          <Route path="bank-accounts" element={<BranchBankAccounts />} />
          <Route path="reports" element={<BranchReports />} />
        </Route>

        {/*  ───────── protected part ────── */}
        <Route element={<ProtectedRoutes />}>
          <Route path="/" element={<Layout />}>

            {/* HOME resolves per-user: supers get /admin-management, everyone
                else the first page their permissions actually allow. */}
            <Route index element={<HomeRedirect />} />

            {/*  ───── separate top-level dashboard if you still need it ─────  */}
            <Route path="admin-dashboard" element={<AdminDashboard />} />

            {/*  ───── client-management group ─────  */}
            <Route path="admin-management" element={<AdminManagementLayout />}>
              <Route index element={<DashboardTab />} />
              <Route path="dashboard" element={<DashboardTab />} />

              <Route path="setting" element={<SettingsTab />} />
              <Route path="myaccount" element={<MyAccountTab />} />
              <Route path="betlist" element={<BetListTab />} />
              <Route path="betlistlive" element={<BetListLiveTab />} />
              <Route path="casino" element={<CasinoTab />} />
              <Route path="riskmanagement" element={<RiskManagementTab />} />
              <Route path="import" element={<ImportTab />} />
              <Route path="message" element={<MessageTab />} />
              <Route path="gamecenter" element={<GameCenterTab />} />
              <Route path="agents" element={<AgentListingTab />} />
              <Route path="transfer" element={<TransferTab />} />
              <Route path="casino-settlement" element={<CasinoSettlementTab />} />
              {/* sports-settlement route removed — sports hidden everywhere */}
              <Route path="access" element={<AccessManagementTab />} />
            </Route>

            {/*  ───── everything else (each route gated via RequirePermission) ───── */}
            <Route path="withdraw" element={<RequirePermission authority="canApproveWithdraw"><WithdrawPage /></RequirePermission>} />
            <Route path="users" element={<RequirePermission><Users /></RequirePermission>} />
            <Route path="settings" element={<RequirePermission><Settings /></RequirePermission>} />
            <Route path="deposit" element={<RequirePermission authority="canApproveDeposit"><DepositPage /></RequirePermission>} />
            <Route path="account-statement" element={<RequirePermission><AccountStatement /></RequirePermission>} />
            <Route path="games" element={<RequirePermission><Game /></RequirePermission>} />
            <Route path="win-settings" element={<RequirePermission><WinSettings /></RequirePermission>} />
            <Route path="house" element={<RequirePermission><House /></RequirePermission>} />
            <Route path="wallet" element={<RequirePermission><Wallet /></RequirePermission>} />
            <Route path="history" element={<RequirePermission><History /></RequirePermission>} />
            <Route path="original-games" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="dashboard" /></RequirePermission>} />
            <Route path="original-games/bets" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="bets" /></RequirePermission>} />
            <Route path="original-games/sessions" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="sessions" /></RequirePermission>} />
            <Route path="original-games/games" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="games" /></RequirePermission>} />
            <Route path="original-games/callbacks" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="callbacks" /></RequirePermission>} />
            <Route path="original-games/validation" element={<RequirePermission authority="canViewOriginalGames"><OriginalGames view="validation" /></RequirePermission>} />
            <Route path="seo-manager" element={<RequirePermission><SEO /></RequirePermission>} />
            <Route path="affiliate" element={<RequirePermission><Referal /></RequirePermission>} />
            <Route path='marketwins' element={<RequirePermission><MarketHistory /></RequirePermission>} />
            <Route path='MOsettle' element={<RequirePermission authority="canSettleSports"><MarketInternalSettle /></RequirePermission>} />
            <Route path='Fansettle' element={<RequirePermission authority="canSettleFancy"><FancySettlement /></RequirePermission>} />
            <Route path='fancyreport' element={<RequirePermission><FancyResultNotShowingFromApi /></RequirePermission>} />
            <Route path='fanwins' element={<RequirePermission><FanWins /></RequirePermission>} />
            <Route path="bonus" element={<RequirePermission authority="canIssueBonus"><Bonus /></RequirePermission>} />
            <Route path="club" element={<RequirePermission><ClubMemberShip /></RequirePermission>} />
            <Route path="banner" element={<RequirePermission><Banner /></RequirePermission>} />
            <Route path="bankdetails" element={<RequirePermission><BankDetails /></RequirePermission>} />
            <Route path="redeemcode" element={<RequirePermission authority="canCreateRedeemCode"><RedeemCode /></RequirePermission>} />
            {/* sportsbeting route removed — sports hidden everywhere */}
            <Route path="exchangerate" element={<RequirePermission><ExchangeRate /></RequirePermission>} />
            <Route path="notification" element={<RequirePermission authority="canSendNotification"><Notification /></RequirePermission>} />
            <Route path="swaphistory" element={<RequirePermission><SwapHistory /></RequirePermission>} />
            <Route path="reports" element={<RequirePermission authority="canViewReports"><Reports /></RequirePermission>} />
            <Route path="kyc" element={<RequirePermission authority="canManageKYC"><KYC /></RequirePermission>} />
            <Route path="tier-management" element={<RequirePermission><TierManagement /></RequirePermission>} />
            <Route path="branch-management" element={<RequirePermission><BranchManagement /></RequirePermission>} />
            <Route path="banking" element={<RequirePermission><BankingPage /></RequirePermission>} />
            <Route path="siteconfig" element={<RequirePermission authority="canEditSiteConfig"><SiteConfig /></RequirePermission>} />
            {/* Gaming module — all 6 views use the unified InnerHousePriority (jsGames API, per GAMING_ROUTES.md) */}
            <Route path="providers-priority" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="rails" /></RequirePermission>} />
            <Route path="vendor-priority" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="vendor" /></RequirePermission>} />
            <Route path="type-priority" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="type" /></RequirePermission>} />
            <Route path="provider-onoff" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="providers" /></RequirePermission>} />
            <Route path="games-typewise" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="typewise" /></RequirePermission>} />
            <Route path="games-edit" element={<RequirePermission authority="canEditPriorities"><InnerHousePriority view="edit" /></RequirePermission>} />
            <Route path="userconfig" element={<RequirePermission><UserConfig /></RequirePermission>} />
            <Route path="winloss" element={<RequirePermission><GameWinLossToggle /></RequirePermission>} />
            <Route path="notification-settings" element={<RequirePermission><NotificationSettings /></RequirePermission>} />
            <Route path="/percentage-hierarchy" element={<RequirePermission><PercentageHierarchyPage /></RequirePermission>} />
            <Route path="giftcard-admin" element={<RequirePermission authority="canIssueGiftCard"><GiftCardAdmin /></RequirePermission>} />
            <Route path="turnover-report" element={<RequirePermission authority="canViewReports"><TurnoverReport /></RequirePermission>} />
            <Route path="vaultpro" element={<RequirePermission><VaultPro /></RequirePermission>} />
            <Route path="peer-trade" element={<RequirePermission><PeerTrade /></RequirePermission>} />
            {/* sports-dashboard / sports-lock / sports-config routes removed — sports hidden everywhere */}
            <Route path="spinwheel" element={<RequirePermission><SpinWheel /></RequirePermission>} />
            <Route path="challenges" element={<RequirePermission><Challenges /></RequirePermission>} />
            <Route path="promotion" element={<RequirePermission><Promotion /></RequirePermission>} />
            <Route path="blog" element={<RequirePermission><BlogAdmin /></RequirePermission>} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
